import { ComponentProps, MouseEvent, ReactNode, useCallback, KeyboardEvent } from 'react';

import { SelectModal, Text, Tooltip } from '@pukka/ui-components';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { twMerge } from 'tailwind-merge';

import { VariantDetail } from './ItemVariantSelector.state';

const SelectModalItemDefault = SelectModal.Item.Default;

type SelectModalItemDefaultProps<T extends VariantDetail, Event extends MouseEvent<HTMLDivElement>> = ComponentProps<
  typeof SelectModalItemDefault<T, Event>
>;

interface ItemVariantSelectorItemProps<T extends VariantDetail>
  extends ComponentProps<
    Exclude<(SelectModalItemDefaultProps<T, MouseEvent<HTMLDivElement>> & { readonly unwrap: true })['children'], ReactNode>
  > {
  readonly children?: ReactNode;
}

interface ItemVariantSelectorParentProps<T extends VariantDetail> extends Pick<ItemVariantSelectorItemProps<T>, 'item' | 'className'> {
  readonly onClick?: () => void;
  readonly selected?: boolean | 'partial';
}

function getItemDisplayPrice(variant: VariantDetail) {
  if (variant.price_min === null && variant.price_max === null) {
    return 'No price inputted';
  }

  if (variant.price_min === variant.price_max) {
    return `Rp ${variant.price_max}`;
  }

  return `Rp ${variant.price_min} - Rp ${variant.price_max}`;
}

function getIsSelected<T>(selected: Set<T>, ...items: T[]) {
  const isEveryItemSelected = items.every((i) => selected.has(i));

  return isEveryItemSelected || (items.some((i) => selected.has(i)) ? 'partial' : false);
}

const CustomSelectModalToggle = SelectModal.Toggle.Factory(getIsSelected);

function ItemVariantSelectorParent({ item, className, selected, onClick }: ItemVariantSelectorParentProps<VariantDetail>) {
  const onKeyUpHandler = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        onClick?.();
      }
    },
    [onClick],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyUp={onKeyUpHandler}
      // Override the background color and text style when partially selected.
      // Must find another way to do this properly.
      className={twMerge(className, selected === 'partial' ? 'bg-inherit text-neutral-500' : '')}
    >
      <div className="flex flex-col">
        <span>{item.item.name}</span>
      </div>
      <div className="flex flex-auto" />
      <SelectModal.Item.Checker selected={!!selected} className={selected === 'partial' ? 'opacity-50' : undefined} />
    </div>
  );
}

function ItemVariantSelectorItem({ item, className, hidden, onClick, children }: ItemVariantSelectorItemProps<VariantDetail>) {
  const onKeyUpHandler = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        onClick();
      }
    },
    [onClick],
  );

  return hidden ? null : (
    <div className="relative">
      {item.siblings.size && item.isFirstChildren ? (
        <CustomSelectModalToggle items={item.item.children} asChild>
          <ItemVariantSelectorParent item={item} className={className} />
        </CustomSelectModalToggle>
      ) : null}
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyUp={onKeyUpHandler}
        className={twMerge(className, 'relative', 'flex flex-auto flex-row', item.siblings.size && '-mt-1 pl-16')}
      >
        {item.siblings.size ? (
          <div className={twMerge('absolute left-12 w-2 border-b border-l', item.isFirstChildren ? 'top-0 h-1/2' : '-top-1/2 h-full')} />
        ) : null}
        <Text className="leading-tight">
          {item.name || item.item.name}
          <br />
          <small className="tracking-wide text-neutral-400">{getItemDisplayPrice(item)}</small>
        </Text>
        {children}
        <div className="flex flex-auto" />
        {item.promo?.promotion_name ? null : <SelectModal.Item.Checker />}
      </div>
      {item.promo?.promotion_name ? (
        <Tooltip
          side="left"
          trigger={
            <div className="absolute right-10 top-1/2 -translate-y-1/2 text-lg text-blue-400 opacity-100">
              <AiOutlineInfoCircle />
            </div>
          }
        >
          <Text color="white" variant="caption-1" className="w-96">
            This item has been assigned to an existing <strong>Moka Order Price Cuts Promo</strong>. Remove item from this promo to
            continue.
            <br />
            <small>
              Assigned to: <em className="text-red-500">{item.promo.promotion_name}</em>
            </small>
          </Text>
        </Tooltip>
      ) : null}
    </div>
  );
}

export { ItemVariantSelectorItem, getItemDisplayPrice as _getItemDisplayPrice, ItemVariantSelectorParent as _ItemVariantSelectorParent };
